import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { VERSION_POLL_MS, setPageBlocked, swallowEscape, versionPrompt } from '../lib/appVersion.js';

// "A new version is ready" (docs/APP-UPDATES.md, "Back office and web"): poll /version.json every
// 5 min and on return to the tab. Every update is required and always on top (owner, 2026-10-14): a
// different build → an overlay above everything (a portal on <body>, the highest z-index), the app
// under it inert, Escape does nothing, Refresh only. Network trouble shows nothing.
const RUNNING = { version: __APP_VERSION__, build: __APP_BUILD__ };

function useLatestVersion(): unknown {
  const [latest, setLatest] = useState<unknown>(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}version.json`, { cache: 'no-store' });
        if (!res.ok) return;
        const body: unknown = await res.json();
        if (alive) setLatest(body);
      } catch {
        // offline or mid-deploy: say nothing, ask again later
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    void check();
    const timer = window.setInterval(check, VERSION_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return latest;
}

const refresh = () => window.location.reload();

function UpdateOverlay({ version }: { version: string }) {
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const root = document.getElementById('root');
    // A modal <dialog> already open sits in the browser's top layer, above any z-index: close it.
    root?.querySelectorAll('dialog[open]').forEach((d) => (d as HTMLDialogElement).close());
    setPageBlocked(root, true);
    document.addEventListener('keydown', swallowEscape, true);
    button.current?.focus();
    return () => {
      setPageBlocked(root, false);
      document.removeEventListener('keydown', swallowEscape, true);
    };
  }, []);
  return createPortal(
    <div className="version-overlay" role="alertdialog" aria-modal="true" aria-labelledby="version-title" aria-describedby="version-body">
      <div className="version-card stack s24">
        <h2 id="version-title" className="t-headline">
          PopCart back office {version} is ready
        </h2>
        <p id="version-body" className="t-body">
          Refresh to keep going. It takes a second.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button ref={button} type="button" className="btn btn-primary" onClick={refresh}>
            Refresh
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function VersionBanner() {
  const prompt = versionPrompt(RUNNING, useLatestVersion());
  return prompt ? <UpdateOverlay version={prompt.version} /> : null;
}
